import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { photographerService } from '../../services/photographerService';
import { reviewService } from '../../services/reviewService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/layout/Header';
import ProfileHero from './components/ProfileHero';
import PortfolioGallery from './components/PortfolioGallery';
import ServicesList from './components/ServicesList';
import ReviewsList from './components/ReviewsList';
import AboutSection from './components/AboutSection';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import { getShootingMethodLabelAr } from '../../utils/bookingLabels';
import {
  HIDDEN_BOOKING_PHOTOGRAPHY_TYPES,
  HIDDEN_PROFILE_SPECIALIZATION_VALUES,
  HIDDEN_SPECIALIZATION_LABELS_AR,
} from '../../utils/photographyTypesVisibility';

const PhotographerProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  // Get photographerId from route param first, then state, then query string
  const photographerIdFromRoute = params.id;
  const photographerIdFromState = location.state?.photographerId;
  const photographerIdFromQuery = searchParams.get('photographerId');
  const photographerId = photographerIdFromRoute || photographerIdFromState || photographerIdFromQuery;
  
  const [activeTab, setActiveTab] = useState('portfolio');
  const [loading, setLoading] = useState(true);
  const [photographerData, setPhotographerData] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ratingBreakdown, setRatingBreakdown] = useState([]);

  useEffect(() => {
    loadPhotographerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photographerIdFromRoute, photographerIdFromState, photographerIdFromQuery]);

  const loadPhotographerData = async () => {
    try {
      setLoading(true);
      
      // Priority: state > query string > current user
      let targetPhotographerId = photographerId;
      
      // If no photographerId from state or query, try to get current user
      if (!targetPhotographerId) {
        try {
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          if (authError) {
            console.warn('Auth error:', authError);
          }
          targetPhotographerId = user?.id;
        } catch (authErr) {
          console.warn('Failed to get current user:', authErr);
        }
      }
      
      if (!targetPhotographerId) {
        console.error('No photographer ID available');
        setLoading(false);
        setPhotographerData(null);
        return;
      }

      console.log('Loading photographer data for ID:', targetPhotographerId);

      // Load photographer profile
      let photographer;
      try {
        photographer = await photographerService.getPhotographer(targetPhotographerId);
      } catch (serviceError) {
        console.error('Error loading photographer from service:', serviceError);
        // Try direct query as fallback (try both user_id and id)
        try {
          // First try by user_id
          let { data: photographerData, error: directError } = await supabase
            .from('photographer_profiles')
            .select('*')
            .eq('user_id', targetPhotographerId)
            .single();
          
          // If not found, try by id
          if (directError && directError.code === 'PGRST116') {
            console.log('📸 Fallback: trying by id instead of user_id');
            const { data: photographerById, error: errorById } = await supabase
              .from('photographer_profiles')
              .select('*')
              .eq('id', targetPhotographerId)
              .single();
            
            if (!errorById && photographerById) {
              photographerData = photographerById;
              directError = null;
            } else {
              directError = errorById || directError;
            }
          }
          
          if (directError) {
            console.error('Direct query error:', directError);
            throw directError;
          }
          
          if (!photographerData) {
            console.error('Photographer not found in database');
            setLoading(false);
            setPhotographerData(null);
            return;
          }

          // Get user profile using user_id from photographer profile
          const userIdForProfile = photographerData.user_id;
          const { data: userProfile, error: userError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, phone, avatar_url')
            .eq('id', userIdForProfile)
            .single();

          photographer = {
            ...photographerData,
            user: userError ? null : userProfile
          };
        } catch (fallbackError) {
          console.error('Fallback query also failed:', fallbackError);
          setLoading(false);
          setPhotographerData(null);
          return;
        }
      }
      
      if (!photographer) {
        console.error('Photographer not found');
        setLoading(false);
        setPhotographerData(null);
        return;
      }

      console.log('Photographer data loaded:', photographer);
      console.log('📸 Photographer user_id:', photographer.user_id);
      console.log('📸 Photographer id:', photographer.id);
      console.log('📸 Target photographer ID:', targetPhotographerId);

      // Get equipment from database (could be array or string)
      const equipmentData = photographer.equipmentAvailable || photographer.equipment_available || [];
      const equipmentArray = Array.isArray(equipmentData) ? equipmentData : [];
      
      // Get shooting methods
      const shootingMethodsData = photographer.shootingMethods || photographer.shooting_methods || [];
      const shootingMethodsArray = Array.isArray(shootingMethodsData) ? shootingMethodsData : [];

      // total_bookings is maintained by a DB trigger on the photographer_profiles table.
      // Querying bookings directly fails for public visitors due to RLS (photographer_id = auth.uid()).
      const completedBookingsCount = photographer.totalBookings ?? photographer.total_bookings ?? 0;
      const repeatCustomersPercentage = photographer.repeatCustomers ?? photographer.repeat_customers ?? 0;

      // Load portfolio images FIRST to use as profile image
      const photographerUserId = photographer.user_id || photographer.userId || targetPhotographerId;
      
      console.log('📸 Loading portfolio images for photographer_id:', photographerUserId);
      
      const { data: portfolioData, error: portfolioError } = await supabase
        .from('portfolio_images')
        .select('*')
        .eq('photographer_id', photographerUserId)
        .eq('approval_status', 'approved') // Only show admin-approved images to public
        .order('is_featured', { ascending: false }) // Featured first
        .order('created_at', { ascending: false }); // Then most recent

      if (portfolioError) {
        console.error('❌ Error loading portfolio images:', portfolioError);
      } else {
        console.log('✅ Portfolio images loaded:', portfolioData?.length || 0, 'images');
      }

      // Get the best portfolio image (featured or most recent)
      const portfolioProfileImage = portfolioData && portfolioData.length > 0 
        ? portfolioData[0].image_url 
        : null;

      // Format photographer data
      const formattedData = {
        id: photographer.userId || photographer.user_id,
        name: photographer.user?.fullName || photographer.user?.full_name || 'مصور',
        // Use portfolio image first, then fallback to avatar
        profileImage: portfolioProfileImage || photographer.user?.avatarUrl || photographer.user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png',
        profileImageAlt: portfolioProfileImage 
          ? `صورة من معرض أعمال ${photographer.user?.fullName || photographer.user?.full_name || 'المصور'}`
          : `صورة ${photographer.user?.fullName || photographer.user?.full_name || 'المصور'}`,
        // Keep avatar separate for small profile picture
        avatar: photographer.user?.avatarUrl || photographer.user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png',
        avatarAlt: `صورة ${photographer.user?.fullName || photographer.user?.full_name || 'المصور'}`,
        city: photographer.city || 'الرياض',
        businessName: photographer.businessName || photographer.business_name || '',
        portfolioUrl: photographer.portfolioUrl || photographer.portfolio_url || '',
        experienceLevel: photographer.experienceLevel || photographer.experience_level || null,
        experienceLevelLabel: getExperienceLabel(photographer.experienceLevel || photographer.experience_level),
        experienceYears: photographer.experienceYears || photographer.experience_years || 0,
        serviceAreaLabel: photographer.serviceAreaLabel || photographer.service_area_label || null,
        rating: parseFloat(photographer.rating) || 0,
        reviewCount: photographer.totalReviews || photographer.total_reviews || 0,
        responseTime: photographer.responseTime || photographer.response_time || 2,
        startingPrice: 0, // Dynamic pricing
        specialties: photographer.specializations || [],
        biography: photographer.bio || 'لا توجد سيرة ذاتية',
        yearsOfExperience: photographer.experienceYears || photographer.experience_years || 0,
        completedBookings: completedBookingsCount,
        repeatCustomers: photographer.repeatCustomers || photographer.repeat_customers || repeatCustomersPercentage,
        equipment: equipmentArray, // Use equipment from database
        shootingMethods: shootingMethodsArray,
        shootingMethodModels: photographer.shootingMethodModels || photographer.shooting_method_models || {},
        contactEmail: photographer.contact_email || photographer.contactEmail || photographer.user?.email || null,
        contact_email: photographer.contact_email || photographer.contactEmail || photographer.user?.email || null,
        specialSkills: photographer.specialSkills || photographer.special_skills || [],
        languages: photographer.languages || []
      };

      setPhotographerData(formattedData);

      // Format and set portfolio items (already loaded above)
      if (!portfolioError && portfolioData && portfolioData.length > 0) {
        const formattedPortfolio = portfolioData.map((img) => ({
          id: img.id,
          image: img.image_url,
          imageAlt: img.description || img.title || 'صورة من المعرض',
          title: img.title || 'بدون عنوان',
          category: img.category || 'التصوير الشخصي'
        }));
        setPortfolioItems(formattedPortfolio);
      } else {
        console.log('⚠️ No portfolio images found for photographer');
        setPortfolioItems([]);
      }

      // Load reviews
      try {
        const reviewsData = await reviewService.getPhotographerReviews(targetPhotographerId);
        if (reviewsData && reviewsData.length > 0) {
          const formattedReviews = reviewsData.map((review) => ({
            id: review.id,
            customerName: review.customer?.fullName || review.customer?.full_name || 'عميل',
            customerImage: review.customer?.avatarUrl || review.customer?.avatar_url || null,
            customerImageAlt: `صورة ${review.customer?.fullName || review.customer?.full_name || 'العميل'}`,
            rating: review.rating || 5,
            date: review.createdAt || review.created_at || new Date().toISOString(),
            comment: review.comment || review.reviewText || '',
            serviceType: review.photographyType || review.photography_type || null
          }));
          setReviews(formattedReviews);

          // Calculate rating breakdown
          const breakdown = [5, 4, 3, 2, 1].map((stars) => {
            const count = formattedReviews.filter((r) => Math.round(r.rating) === stars).length;
            const percentage = formattedReviews.length > 0 
              ? Math.round((count / formattedReviews.length) * 100) 
              : 0;
            return { stars, count, percentage };
          });
          setRatingBreakdown(breakdown);
        }
      } catch (reviewError) {
        console.warn('Failed to load reviews:', reviewError);
        // Keep default empty reviews
        setReviews([]);
        setRatingBreakdown([]);
      }

      console.log('Photographer profile loaded successfully');
    } catch (error) {
      console.error('Failed to load photographer data:', error);
      // Set error state
      setPhotographerData(null);
      setPortfolioItems([]);
      setReviews([]);
      setRatingBreakdown([]);
    } finally {
      setLoading(false);
    }
  };


  // Map specializations to services dynamically
  const getServicesFromSpecializations = (specializations) => {
    const serviceMap = {
      'حفلات الزفاف': {
        name: "تصوير حفلات الزفاف",
        description: "تغطية كاملة لحفل الزفاف مع مصور ومساعد وجميع المعدات اللازمة",
        icon: "Heart",
        features: ["تغطية كاملة للحفل", "مصور + مساعد", "تسليم 300+ صورة معدلة", "ألبوم رقمي فاخر"]
      },
      'التصوير الشخصي': {
        name: "التصوير الشخصي",
        description: "جلسة تصوير شخصية احترافية في الاستوديو أو الموقع المفضل",
        icon: "User",
        features: ["جلسة تصوير احترافية", "تسليم 30+ صورة معدلة", "اختيار الموقع", "استشارة مجانية"]
      },
      'تصوير المناسبات': {
        name: "تصوير المناسبات",
        description: "تغطية المناسبات الخاصة والاحتفالات العائلية",
        icon: "Calendar",
        features: ["تغطية شاملة", "تسليم 150+ صورة", "تصوير فيديو قصير", "تسليم سريع"]
      },
      'تصوير المنتجات': {
        name: "تصوير المنتجات",
        description: "تصوير احترافي للمنتجات مع إضاءة وخلفيات متخصصة",
        icon: "Package",
        features: ["تصوير احترافي", "خلفيات متعددة", "تعديل احترافي", "صور عالية الجودة"]
      },
      'تصوير الطعام': {
        name: "تصوير الطعام",
        description: "تصوير احترافي للأطباق والمطاعم",
        icon: "Utensils",
        features: ["تصوير احترافي", "إضاءة متخصصة", "تعديل احترافي", "صور جذابة"]
      },
      'تصوير العقارات': {
        name: "تصوير العقارات",
        description: "تصوير احترافي للعقارات والمنازل",
        icon: "Home",
        features: ["تصوير شامل", "صور داخلية وخارجية", "جودة عالية", "تسليم سريع"]
      },
      'التصوير المعماري': {
        name: "التصوير المعماري",
        description: "تصوير المباني والتصاميم المعمارية",
        icon: "Building",
        features: ["تصوير احترافي", "زوايا متعددة", "جودة عالية", "تعديل احترافي"]
      },
      'تصوير الأطفال': {
        name: "تصوير الأطفال",
        description: "جلسات تصوير احترافية للأطفال",
        icon: "Baby",
        features: ["جلسات ممتعة", "صور طبيعية", "تسليم سريع", "جودة عالية"]
      },
      'التصوير الصحفي': {
        name: "التصوير الصحفي",
        description: "تغطية الأحداث والأخبار",
        icon: "Newspaper",
        features: ["تغطية شاملة", "لقطات سريعة", "تسليم فوري", "جودة عالية"]
      }
    };

    if (!specializations || specializations.length === 0) {
      return [];
    }

    const visibleSpecs = specializations.filter((spec) => {
      if (HIDDEN_SPECIALIZATION_LABELS_AR.has(spec)) return false;
      if (HIDDEN_BOOKING_PHOTOGRAPHY_TYPES.has(spec)) return false;
      if (HIDDEN_PROFILE_SPECIALIZATION_VALUES.has(spec)) return false;
      return true;
    });

    return visibleSpecs.map((spec, index) => {
      const serviceInfo = serviceMap[spec] || {
        name: spec,
        description: `خدمة ${spec} احترافية`,
        icon: "Camera",
        features: ["خدمة احترافية", "جودة عالية", "تسليم سريع"]
      };

      return {
        id: index + 1,
        ...serviceInfo,
        pricingNote: "السعر يُحسب ديناميكياً حسب المدينة والمدة والمتطلبات"
      };
    });
  };

  // Generate services from photographer's specializations
  const services = photographerData ? getServicesFromSpecializations(photographerData.specialties) : [];


  // Map experience level to Arabic label
  const getExperienceLabel = (level) => {
    const levelMap = {
      'certified': 'معتمد',
      'distinguished': 'مميز',
      'elite': 'نخبة',
      // Legacy mappings
      'beginner': 'معتمد',
      'intermediate': 'مميز',
      'advanced': 'مميز',
      'expert': 'نخبة'
    };
    return levelMap[level] || level || 'غير محدد';
  };

  const handleBookNow = () => {
    if (!photographerData) return;
    // Navigate to booking wizard with direct mode and photographer preselected
    const targetPhotographerId = photographerId || photographerData.id || photographerData.user_id;
    navigate(`/new-booking?mode=direct&photographerId=${targetPhotographerId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!photographerData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 px-4">
          <Icon name="UserX" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground">لم يتم العثور على المصور</h2>
          <p className="text-muted-foreground">
            {photographerId 
              ? `المصور المحدد غير موجود في قاعدة البيانات`
              : 'لم يتم تحديد معرف المصور'}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/browse-photographers')}
            iconName="ArrowRight"
            iconPosition="right"
          >
            العودة إلى قائمة المصورين
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
  { id: 'portfolio', label: 'المعرض', icon: 'Image' },
  { id: 'services', label: 'الخدمات', icon: 'Briefcase' },
  { id: 'reviews', label: 'التقييمات', icon: 'Star' },
  { id: 'about', label: 'نبذة عني', icon: 'User' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-6">
            <BackButton to="/browse-photographers" label="العودة إلى المصورين" />
          </div>

          {/* Profile Hero */}
          <ProfileHero photographer={photographerData} onBookNow={handleBookNow} />

          {/* Tabs Navigation */}
          <div className="mt-8 bg-card rounded-xl shadow-warm-sm p-2 overflow-x-auto">
            <div className="flex gap-2 min-w-max lg:min-w-0">
              {tabs?.map((tab) => {
                const isActive = activeTab === tab?.id;
                return (
                <button
                  key={tab?.id}
                  type="button"
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm md:text-base font-medium transition-smooth whitespace-nowrap border-2 bg-transparent ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted hover:border-border'
                  }`}
                >
                  <Icon
                    name={tab?.icon}
                    size={20}
                    className={isActive ? 'text-primary' : 'text-inherit'}
                  />
                  <span>{tab?.label}</span>
                </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6 md:mt-8">
            {activeTab === 'portfolio' &&
            <PortfolioGallery portfolioItems={portfolioItems} />
            }
            {activeTab === 'services' &&
            (services.length > 0 ? (
              <ServicesList services={services} />
            ) : (
              <div className="bg-card rounded-xl p-8 md:p-12 text-center">
                <Icon name="Briefcase" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                  لا توجد خدمات متاحة
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  لم يتم تحديد التخصصات بعد
                </p>
              </div>
            ))
            }
            {activeTab === 'reviews' &&
            (reviews.length > 0 ? (
              <ReviewsList
                reviews={reviews}
                overallRating={photographerData?.rating}
                ratingBreakdown={ratingBreakdown} />
            ) : (
              <div className="bg-card rounded-xl p-8 md:p-12 text-center">
                <Icon name="Star" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                  لا توجد تقييمات بعد
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  كن أول من يقيم هذا المصور
                </p>
              </div>
            ))
            }
            {activeTab === 'about' &&
            <AboutSection photographer={photographerData} />
            }
          </div>

        </div>
      </div>
    </div>);

};

export default PhotographerProfile;